/**
 * Estimated Comms Degradation detail panel (Ice Storm Risk → "Estimated Comms
 * Degradation"; #109). A FUSION-DERIVED ESTIMATE from the `comms_degradation_zones`
 * table — NOT an actionable incident (no R.3 dispatch actions). The zone is the
 * estimated cell-availability-loss area from a sustained power-outage cluster (cell
 * sites run ~2–8 h on battery), corroborated by freezing-rain CAP + the #108 telecom
 * ASN tripwire (operator-cop-doctrine §2.1 comms/reach-back, IMS §2).
 *
 * HONEST FRAMING is the headline: a prominent "ESTIMATE" banner + the confidence
 * band, the contributing signals, and — the operational payoff — the "probably
 * unreachable communities" list (unreachable-resident accountability) with their
 * vulnerable-population counts. Colour is always paired with the confidence label
 * (§3A.7).
 */
import { useEffect } from 'react';
import type { CommsDegradationZoneFeature } from '@tsr/shared-types';
import { commsZoneStyle } from './styles/commsStyles';
import { logAction } from './lib/operatorActions';

function agoLabel(iso: string | null): string {
  if (!iso) return 'unknown';
  const ms = Date.now() - Date.parse(iso);
  if (!Number.isFinite(ms)) return 'unknown';
  const min = Math.round(ms / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const h = Math.round(min / 60);
  if (h < 48) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

export function CommsDegradationZoneBody({
  zone,
}: {
  zone: CommsDegradationZoneFeature;
}): JSX.Element {
  const p = zone.properties;
  const [lon, lat] = zone.centroid;
  const style = commsZoneStyle(p.estimate_confidence);

  useEffect(() => {
    logAction('click_comms_degradation_zone', {
      zone_id: zone.id,
      estimate_confidence: p.estimate_confidence,
    });
  }, [zone.id, p.estimate_confidence]);

  const signals: string[] = [];
  if (p.has_asn_tripwire) {
    signals.push(
      p.asn_carriers.length ? `ASN tripwire (${p.asn_carriers.join(', ')})` : 'ASN tripwire',
    );
  }
  if (p.has_freezing_rain) signals.push('Freezing-rain warning');
  signals.push(`${p.outage_count} clustered power outage${p.outage_count === 1 ? '' : 's'}`);

  return (
    <div className="px-4 py-4 space-y-4" data-testid="comms-degradation-zone-body">
      <div className="border-l-2 pl-3" style={{ borderColor: style.color }}>
        <p className="text-[10px] uppercase tracking-wider text-slate-500">
          Estimated comms degradation
        </p>
        <h2 className="text-base font-semibold">Probable cell-availability loss</h2>
        <span
          className="inline-flex items-center gap-1.5 mt-1.5 text-[11px] px-2 py-0.5 rounded font-medium"
          style={{ backgroundColor: `${style.color}22`, color: style.color }}
        >
          <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: style.color }} />
          {style.label}
        </span>
      </div>

      {/* The honesty banner — this is an estimate, never measured coverage. */}
      <p
        className="text-[11px] leading-relaxed rounded px-2.5 py-2"
        style={{ backgroundColor: `${style.color}14`, color: style.color }}
      >
        ⚠ ESTIMATE — a buffered power-outage cluster, not measured network coverage.
        Cell sites typically run ~2–8&nbsp;h on battery after grid loss, then go dark.
        Blind to localized tower loss; treat as a planning aid, not ground truth.
      </p>

      <section>
        <h3 className="text-xs uppercase tracking-wider text-slate-400 mb-1.5">Why this zone</h3>
        <ul className="text-sm list-disc pl-5 space-y-0.5 text-slate-300">
          {signals.map((s) => (
            <li key={s}>{s}</li>
          ))}
        </ul>
      </section>

      <section>
        <h3 className="text-xs uppercase tracking-wider text-slate-400 mb-1.5">Details</h3>
        <dl className="grid grid-cols-[9rem_1fr] gap-y-1 text-sm">
          <dt className="text-slate-400">Confidence</dt>
          <dd>{style.label}</dd>
          <dt className="text-slate-400">Outage age</dt>
          <dd>{p.elapsed_hours == null ? '—' : `${p.elapsed_hours.toFixed(1)} h on battery`}</dd>
          <dt className="text-slate-400">Outages</dt>
          <dd>{p.outage_count}</dd>
          <dt className="text-slate-400">Customers out</dt>
          <dd>{p.customers_affected == null ? '—' : p.customers_affected.toLocaleString()}</dd>
          <dt className="text-slate-400">Est. area</dt>
          <dd>{p.area_km2 == null ? '—' : `${p.area_km2.toLocaleString()} km²`}</dd>
          <dt className="text-slate-400">Utilities</dt>
          <dd>{p.utilities.length ? p.utilities.join(', ') : '—'}</dd>
          <dt className="text-slate-400">Centroid</dt>
          <dd className="font-mono text-xs">
            {lat.toFixed(3)}, {lon.toFixed(3)}
          </dd>
        </dl>
      </section>

      <section>
        <h3 className="text-xs uppercase tracking-wider text-slate-400 mb-1.5">
          Probably unreachable communities ({p.communities_affected})
        </h3>
        {p.affected_communities.length === 0 ? (
          <p className="text-sm text-slate-500">No mapped communities inside the estimated zone.</p>
        ) : (
          <ul className="text-sm space-y-1">
            {p.affected_communities.map((c) => (
              <li key={c.id} className="flex justify-between gap-2">
                <span>{c.name}</span>
                <span className="text-xs text-slate-400">
                  {c.population != null ? `pop ${c.population.toLocaleString()}` : 'pop —'}
                  {c.vulnerable_population != null
                    ? ` · ${c.vulnerable_population.toLocaleString()} vulnerable`
                    : ''}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <footer className="border-t border-slate-800 pt-3 text-[10px] text-slate-500 leading-relaxed space-y-0.5">
        <div>First detected {agoLabel(p.first_detected_at)} · refreshed {agoLabel(p.last_seen_at)}</div>
        <div>
          Fusion estimate — power-outage clusters + cell-tower battery-decay model,
          corroborated by freezing-rain CAP + telecom (IODA) ASN tripwire.
        </div>
      </footer>
    </div>
  );
}
